import { useMemo } from 'react';
import { useWorkspace } from '../../../../contexts/WorkspaceContext';
import { useInfiniteProjects, type Project } from '../../../../hooks/data/useProjects';
import { useDebounce } from '../../../../hooks/ui/useDebounce';
import { workspaceDestination, type CreateDestination } from './createDestination';

type DestinationRow = Project & {
  readonly canEdit?: boolean;
  readonly privacyMode?: 'RESTRICTED' | 'PRIVATE';
  readonly parentName?: string;
};

/**
 * The create modal's "Save to..." results: the workspace root (while it
 * matches the search) and collections searched on the server, paged at 50
 * with "Show more", each carrying whether the caller can add to it.
 */
export function useCreateDestinationResults(query: string) {
  const { workspace } = useWorkspace();
  const search = useDebounce(query.trim(), 300);
  const list = useInfiniteProjects({ searchTerm: search || undefined, include: 'destination' });
  // Rows from an earlier query (debounce pending, placeholder page) would
  // offer collections that do not match; show none until this one answers.
  const settled = search === query.trim() && !list.isPlaceholderData;
  const collections = useMemo<CreateDestination[]>(
    () =>
      settled
        ? (list.flatItems as DestinationRow[]).map((row) => ({
            type: 'project',
            id: row.id,
            name: row.name,
            canEdit: row.canEdit,
            privacyMode: row.privacyMode,
            parentName: row.parentName,
          }))
        : [],
    [list.flatItems, settled],
  );
  const root =
    workspace && workspace.name.toLowerCase().includes(query.trim().toLowerCase())
      ? workspaceDestination(workspace)
      : null;
  return {
    collections,
    root,
    isLoading: list.isLoading,
    isSearching: query.trim().length > 0,
    hasMore: settled && !!list.hasNextPage,
    isLoadingMore: list.isFetchingNextPage,
    loadMore: () => void list.fetchNextPage(),
  };
}
